import { Link } from 'react-router-dom';
import { useIntl } from '@edx/frontend-platform/i18n';
import { ErrorPage } from '@edx/frontend-platform/react';
import { getConfig } from '@edx/frontend-platform';

import { useCourseListSearch } from '@src/data/course-list-search/hooks';
import { AlertNotification } from '@src/generic';
import { DEFAULT_PAGE_INDEX } from '@src/data/course-list-search/constants';
import HomeCourseCardSlot from '@src/plugin-slots/HomeCourseCardSlot';
import { LoaderSlot } from '@src/plugin-slots/LoaderSlot';
import { ROUTES } from '@src/routes';

import messages from './messages';

const DEFAULT_HOME_COURSE_COUNT = 4;

const CoursesList = () => {
  const intl = useIntl();
  const config = getConfig();

  const configuredCount = Number(config.HOMEPAGE_COURSE_MAX || DEFAULT_HOME_COURSE_COUNT);
  const maxCourses = Math.min(configuredCount || DEFAULT_HOME_COURSE_COUNT, DEFAULT_HOME_COURSE_COUNT);

  const {
    data: courseData,
    isLoading: isCoursesLoading,
    isError: isCoursesError,
  } = useCourseListSearch({
    pageSize: maxCourses,
    pageIndex: DEFAULT_PAGE_INDEX,
    enableCourseSortingByStartDate: config.ENABLE_COURSE_SORTING_BY_START_DATE || false,
  });

  if (config.NON_BROWSABLE_COURSES) {
    return null;
  }

  return (
    <section className="catalog-home-section catalog-home-section--popular" data-testid="courses-list">
      <div className="catalog-home-section__container">
        <div className="catalog-home-section__header">
          <div className="catalog-home-section__copy">
            <span className="catalog-home-section__eyebrow">{intl.formatMessage(messages.eyebrow)}</span>
            <h2 className="catalog-home-section__title">{intl.formatMessage(messages.title)}</h2>
          </div>
          <Link className="catalog-ui-button catalog-ui-button--secondary catalog-ui-button--surface" to={ROUTES.COURSES}>
            {intl.formatMessage(messages.viewAllCoursesButton)}
          </Link>
        </div>

        {isCoursesLoading ? (
          <LoaderSlot>
            <div className="catalog-home-courses-grid">
              {Array.from({ length: maxCourses }, (_, index) => (
                <HomeCourseCardSlot key={`courses-list-loading-skeleton-card-${index}`} isLoading />
              ))}
            </div>
          </LoaderSlot>
        ) : null}

        {isCoursesError ? (
          <div className="catalog-home-message-shell">
            <AlertNotification
              className="my-0"
              variant="danger"
              title={intl.formatMessage(messages.errorTitle)}
              message={(
                <ErrorPage
                  message={intl.formatMessage(messages.errorMessage, {
                    supportEmail: config.INFO_EMAIL,
                  })}
                />
              )}
            />
          </div>
        ) : null}

        {!isCoursesLoading && !isCoursesError ? (
          !courseData?.results?.length ? (
            <div className="catalog-home-message-shell">
              <AlertNotification
                className="my-0"
                variant="info"
                title={intl.formatMessage(messages.noCoursesAvailable)}
                message={intl.formatMessage(messages.noCoursesAvailableMessage)}
              />
            </div>
          ) : (
            <div className="catalog-home-courses-grid">
              {courseData.results.map(course => (
                <HomeCourseCardSlot key={course.id} original={course} />
              ))}
            </div>
          )
        ) : null}
      </div>
    </section>
  );
};

export default CoursesList;
